// PRD 10 §3.7 — content block editor: add Text / Image / Video blocks, edit
// each in place, delete, and reorder by drag-and-drop. Drag-and-drop uses the
// native HTML5 API (no extra dependency); only the ≡ handle is draggable so it
// never fights the rich-text caret or the inputs.
//
// Image block (PRD v2.6 §3.7): one image per block, JPG/PNG only, and NOT
// replaceable — once uploaded the drop-zone stops accepting clicks; to change
// the image the user deletes the block and adds a new one.

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, Trash2 } from 'lucide-react';

import { toast } from '@/components/feedback/useToast';

import { useUploadImage } from '../hooks/use-health-encyclopedia-mutations';
import type { ContentBlock } from '../types/health-encyclopedia.types';
import { getEncyclopediaErrorMessage } from '../utils/error-message';
import {
  BLOCK_DESC_MAX,
  IMAGE_ACCEPT,
  IMAGE_TITLE_MAX,
  MAX_BLOCKS,
  VIDEO_TITLE_MAX,
  isImageFileValid,
} from '../utils/validate-form';
import { EncyclopediaRichTextEditor } from './EncyclopediaRichTextEditor';

interface EncyclopediaBlockListProps {
  blocks: ContentBlock[];
  disabled?: boolean;
  onAddText: () => void;
  onAddImage: () => void;
  onAddVideo: () => void;
  onChangeBlock: (id: number, patch: Partial<ContentBlock>) => void;
  onDeleteBlock: (id: number) => void;
  onReorder: (from: number, to: number) => void;
}

function ImageBlockBody({
  block,
  disabled = false,
  onChange,
}: {
  block: ContentBlock;
  disabled?: boolean;
  onChange: (patch: Partial<ContentBlock>) => void;
}) {
  const { t } = useTranslation('health-encyclopedia');
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadImage();

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    // PRD §3.7 / §8.1 — JPG/PNG only, 최대 5MB. Pre-checked client-side so the user gets the
    // message immediately instead of after a full upload round-trip (the BE re-validates
    // authoritatively with a magic-byte sniff). `accept` alone is not a check: the OS file
    // dialog lets the user switch to "all files". On any violation the block and its existing
    // image are kept, and ONE unified message covers both format and size — upload errors are
    // the documented toast exception in admin-web §7.1.
    if (!isImageFileValid(file)) {
      toast.error(t('errors.UPLOAD_FAILED'));
      return;
    }
    try {
      const { url } = await upload.mutateAsync(file);
      onChange({ dataUrl: url });
    } catch (e) {
      toast.error(getEncyclopediaErrorMessage(e, t, 'errors.UPLOAD_FAILED'));
    }
  };

  // PRD v2.6 §3.7 — the image block is not replaceable: once an image is set the
  // drop-zone no longer opens the file picker (delete the block to change it).
  const locked = disabled || Boolean(block.dataUrl);

  return (
    <>
      {/* PRD v2.5 §3.7 — optional block title (outside the drop-zone so typing
          here never opens the file picker). */}
      <input
        type="text"
        className="form-field__input"
        value={block.title ?? ''}
        maxLength={IMAGE_TITLE_MAX}
        placeholder={t('blocks.imageTitlePlaceholder')}
        disabled={disabled}
        style={{ marginBottom: 6 }}
        onChange={(e) => onChange({ title: e.target.value })}
      />

      <div
        onClick={() => !locked && inputRef.current?.click()}
        style={{
          border: '1px dashed var(--border)',
          borderRadius: 'var(--radius-sm)',
          padding: 18,
          textAlign: 'center',
          background: 'var(--muted)',
          cursor: locked ? 'default' : 'pointer',
        }}
      >
        {/* Prototype parity: the image *block* drop-zone shows only the uploaded
            image (if any) + the hint — no placeholder icon (unlike the thumbnail
            zone, which keeps its image-plus icon). */}
        {block.dataUrl ? (
          <img
            src={block.dataUrl}
            alt=""
            style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 6, display: 'block', margin: '0 auto 8px' }}
          />
        ) : null}
        <div style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
          {upload.isPending
            ? '…'
            : block.dataUrl
              ? t('blocks.imageSetHint')
              : t('blocks.imageUploadHint')}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => {
          void handleFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      {/* PRD v2.5 §3.7 — "텍스트 설명 노출" toggle + optional description. */}
      <BlockDescription block={block} disabled={disabled} onChange={onChange} descKey="blocks.imageDescPlaceholder" />
    </>
  );
}

// Shared "텍스트 설명 노출" toggle + description textarea for image/video blocks
// (PRD v2.5 §3.7 — same spec as 관리지침/상담매뉴얼). The description lives on
// `block.text` and is only surfaced in the preview when `showText` is on.
function BlockDescription({
  block,
  disabled = false,
  onChange,
  descKey,
}: {
  block: ContentBlock;
  disabled?: boolean;
  onChange: (patch: Partial<ContentBlock>) => void;
  descKey: string;
}) {
  const { t } = useTranslation('health-encyclopedia');

  return (
    <>
      <label
        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', margin: '6px 0 4px' }}
      >
        <input
          type="checkbox"
          checked={block.showText ?? false}
          disabled={disabled}
          style={{ width: 14, height: 14, accentColor: 'var(--info)' }}
          onChange={(e) => onChange({ showText: e.target.checked })}
        />
        {t('blocks.showText')}
      </label>
      {block.showText ? (
        <textarea
          className="form-field__textarea"
          style={{ minHeight: 60 }}
          value={block.text ?? ''}
          maxLength={BLOCK_DESC_MAX}
          placeholder={t(descKey)}
          disabled={disabled}
          onChange={(e) => onChange({ text: e.target.value })}
        />
      ) : null}
    </>
  );
}

export function EncyclopediaBlockList({
  blocks,
  disabled = false,
  onAddText,
  onAddImage,
  onAddVideo,
  onChangeBlock,
  onDeleteBlock,
  onReorder,
}: EncyclopediaBlockListProps) {
  const { t } = useTranslation('health-encyclopedia');
  const dragIndex = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  // Clarification ADM.015 — at most 10 blocks per content; block adding once reached.
  const atBlockLimit = blocks.length >= MAX_BLOCKS;

  const handleDrop = (target: number) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    setDragOver(null);
    if (from == null || from === target) return;
    onReorder(from, target);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
        <button type="button" className="btn btn-default btn-secondary" disabled={disabled || atBlockLimit} onClick={onAddText}>
          T {t('blocks.addText')}
        </button>
        <button type="button" className="btn btn-default btn-secondary" disabled={disabled || atBlockLimit} onClick={onAddImage}>
          🖼 {t('blocks.addImage')}
        </button>
        <button type="button" className="btn btn-default btn-secondary" disabled={disabled || atBlockLimit} onClick={onAddVideo}>
          🎬 {t('blocks.addVideo')}
        </button>
        {atBlockLimit ? (
          <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
            {t('blocks.limitReached', { max: MAX_BLOCKS })}
          </span>
        ) : null}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          padding: 8,
          background: '#fff',
          minHeight: 80,
        }}
      >
        {blocks.length === 0 ? (
          <div
            style={{
              padding: 16,
              border: '1px dashed var(--border)',
              borderRadius: 'var(--radius-sm)',
              textAlign: 'center',
              color: 'var(--muted-foreground)',
              fontSize: 13,
            }}
          >
            {t('blocks.empty')}
          </div>
        ) : (
          blocks.map((block, index) => (
            <div
              key={block.id}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(index);
              }}
              onDrop={() => handleDrop(index)}
              style={{
                border: `1px solid ${dragOver === index ? 'var(--info)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)',
                padding: 8,
                background: '#fff',
              }}
            >
              {/* Prototype parity: position label on the left; drag handle (≡)
                  grouped next to the delete button on the right. */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
                  {t('blocks.position', { index: index + 1 })}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    title={t('blocks.dragHandle')}
                    aria-label={t('blocks.dragHandle')}
                    draggable={!disabled}
                    onDragStart={() => {
                      dragIndex.current = index;
                    }}
                    onDragEnd={() => {
                      dragIndex.current = null;
                      setDragOver(null);
                    }}
                    style={{ cursor: 'grab', padding: '2px 6px' }}
                  >
                    <Menu size={16} />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    title={t('blocks.delete')}
                    aria-label={t('blocks.delete')}
                    disabled={disabled}
                    style={{ color: 'var(--destructive)', padding: '2px 8px' }}
                    onClick={() => onDeleteBlock(block.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </span>
              </div>

              {block.type === 'text' ? (
                <EncyclopediaRichTextEditor
                  blockId={block.id}
                  value={block.text}
                  placeholder={t('blocks.bodyPlaceholder')}
                  readOnly={disabled}
                  onChange={(html) => onChangeBlock(block.id, { text: html })}
                />
              ) : block.type === 'image' ? (
                <ImageBlockBody
                  block={block}
                  disabled={disabled}
                  onChange={(patch) => onChangeBlock(block.id, patch)}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {/* ADM.018 — optional video title (1~50 chars). */}
                  <input
                    type="text"
                    className="form-field__input"
                    value={block.title ?? ''}
                    maxLength={VIDEO_TITLE_MAX}
                    placeholder={t('blocks.videoTitlePlaceholder')}
                    disabled={disabled}
                    onChange={(e) => onChangeBlock(block.id, { title: e.target.value })}
                  />
                  <input
                    type="text"
                    className="form-field__input"
                    value={block.url ?? ''}
                    placeholder={t('blocks.videoUrlPlaceholder')}
                    disabled={disabled}
                    onChange={(e) => onChangeBlock(block.id, { url: e.target.value })}
                  />
                  {/* PRD v2.5 §3.7 — "텍스트 설명 노출" toggle + optional description. */}
                  <BlockDescription
                    block={block}
                    disabled={disabled}
                    onChange={(patch) => onChangeBlock(block.id, patch)}
                    descKey="blocks.videoDescPlaceholder"
                  />
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
